<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactsDetailsLayout from 'dashboard/components-next/Contacts/ContactsDetailsLayout.vue';
import ContactDetails from 'dashboard/components-next/Contacts/Pages/ContactDetails.vue';
import ContactNotes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactNotes.vue';
import ContactHistory from 'dashboard/components-next/Contacts/ContactsSidebar/ContactHistory.vue';
import ContactMerge from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMerge.vue';
import ContactCustomAttributes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactCustomAttributes.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const contactById = useMapGetter('contacts/getContactById');
const uiFlags = useMapGetter('contacts/getUIFlags');

const activeTab = ref('attributes');

const isFetchingItem = computed(() => uiFlags.value.isFetchingItem);
const isMergingContact = computed(() => uiFlags.value.isMerging);
const isUpdatingContact = computed(() => uiFlags.value.isUpdating);

const selectedContact = computed(() =>
  contactById.value(route.params.contactId)
);

const showSpinner = computed(
  () => isFetchingItem.value || isMergingContact.value
);

const CONTACT_TABS = computed(() => [
  {
    key: 'attributes',
    label: t('CONTACTS_LAYOUT.SIDEBAR.TABS.ATTRIBUTES'),
    icon: 'i-ph-list-bold',
  },
  {
    key: 'history',
    label: t('CONTACTS_LAYOUT.SIDEBAR.TABS.HISTORY'),
    icon: 'i-ph-clock-counter-clockwise-bold',
  },
  {
    key: 'notes',
    label: t('CONTACTS_LAYOUT.SIDEBAR.TABS.NOTES'),
    icon: 'i-ph-note-bold',
  },
  {
    key: 'merge',
    label: t('CONTACTS_LAYOUT.SIDEBAR.TABS.MERGE'),
    icon: 'i-ph-git-merge-bold',
  },
]);

const goToContactsList = () => {
  const baseParams = { accountId: route.params.accountId };

  if (route.name === 'contacts_edit_segment' && route.params.segmentId) {
    router.push({
      name: 'contacts_dashboard_segments_index',
      params: { ...baseParams, segmentId: route.params.segmentId },
      query: { page: 1 },
    });
    return;
  }

  if (route.name === 'contacts_edit_label' && route.params.label) {
    router.push({
      name: 'contacts_dashboard_labels_index',
      params: { ...baseParams, label: route.params.label },
      query: { page: 1 },
    });
    return;
  }

  router.push({
    name: 'contacts_dashboard_index',
    params: baseParams,
    query: { page: 1 },
  });
};

const fetchActiveContact = async () => {
  if (!route.params.contactId) return;
  await store.dispatch('contacts/show', { id: route.params.contactId });
};

const fetchContactNotes = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactNotes/get', { contactId });
};

const fetchContactConversations = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactConversations/get', contactId);
};

const fetchAttributes = () => {
  store.dispatch('attributes/get');
};

const handleTabChange = tabKey => {
  activeTab.value = tabKey;
};

const toggleContactBlock = async isCurrentlyBlocked => {
  const ALERT_MESSAGES = {
    success: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_SUCCESS_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_SUCCESS_MESSAGE'),
    },
    error: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_ERROR_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_ERROR_MESSAGE'),
    },
  };

  try {
    await store.dispatch('contacts/update', {
      ...selectedContact.value,
      blocked: !isCurrentlyBlocked,
    });
    useAlert(
      isCurrentlyBlocked
        ? ALERT_MESSAGES.success.unblock
        : ALERT_MESSAGES.success.block
    );
  } catch {
    useAlert(
      isCurrentlyBlocked
        ? ALERT_MESSAGES.error.unblock
        : ALERT_MESSAGES.error.block
    );
  }
};

onMounted(() => {
  fetchActiveContact();
  fetchContactNotes();
  fetchContactConversations();
  fetchAttributes();
});
</script>

<template>
  <div class="w-full h-full">
    <div
      v-if="showSpinner"
      class="flex items-center justify-center h-full text-n-slate-11"
    >
      <Spinner />
    </div>

    <ContactsDetailsLayout
      v-else-if="selectedContact"
      :selected-contact="selectedContact"
      :is-updating="isUpdatingContact"
      @go-to-contacts-list="goToContactsList"
      @toggle-block="toggleContactBlock"
    >
      <template #default>
        <ContactDetails
          :selected-contact="selectedContact"
          @go-to-contacts-list="goToContactsList"
        />
      </template>

      <template #sidebar>
        <div class="flex flex-col h-full">
          <div class="px-2 pt-2">
            <div class="flex items-center gap-1 px-4">
              <button
                v-for="tab in CONTACT_TABS"
                :key="tab.key"
                type="button"
                class="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                :class="
                  activeTab === tab.key
                    ? 'bg-n-alpha-2 text-n-slate-12'
                    : 'text-n-slate-11 hover:bg-n-alpha-2'
                "
                @click="handleTabChange(tab.key)"
              >
                <span class="size-4" :class="[tab.icon]" />
                {{ tab.label }}
              </button>
            </div>
          </div>

          <div class="flex-1 min-h-0 border-t border-n-weak">
            <ContactCustomAttributes
              v-if="activeTab === 'attributes'"
              :selected-contact="selectedContact"
            />
            <ContactHistory v-else-if="activeTab === 'history'" />
            <ContactNotes v-else-if="activeTab === 'notes'" />
            <ContactMerge
              v-else
              :selected-contact="selectedContact"
              @go-to-contacts-list="goToContactsList"
              @reset-tab="handleTabChange('attributes')"
            />
          </div>
        </div>
      </template>
    </ContactsDetailsLayout>

    <div v-else class="flex items-center justify-center h-full text-n-slate-11">
      {{ t('CONTACT_PANEL.NOT_AVAILABLE') }}
    </div>
  </div>
</template>
